import React from 'react';
import { Chip } from '@mui/material';
import { ShieldAlert, ShieldCheck, ShieldOff, Link2Off, ClockAlert, LockOpen, Nfc } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ScanStatusMeta, ScanStatusIcon } from '@repo/ui';

/**
 * Icon-name → component. The resolver in `@repo/ui` names the glyph but can't import
 * one (it's a framework-free module shared by two apps), so the mapping lives here.
 */
// VRG-252 — the value type was `ComponentType<{size?, color?}>`, which no lucide icon satisfies
// (they are `ForwardRefExoticComponent<LucideProps & RefAttributes<SVGSVGElement>>`), so every
// entry in this map was a TS2322. Widened to the icons' own type: the call site passes exactly
// `size` and `color`, both of which `LucideProps` accepts, so nothing about usage changes.
const ICONS: Record<ScanStatusIcon, LucideIcon> = {
  'shield-alert': ShieldAlert,
  // VERA-260: verified's own glyph. Unreachable from this component today (the chip
  // only renders when `showChip` is true, and verified's is false) — present because
  // the map is exhaustive over `ScanStatusIcon`.
  'shield-check': ShieldCheck,
  'shield-off': ShieldOff,
  'link-2-off': Link2Off,
  'clock-alert': ClockAlert,
  'lock-open': LockOpen,
  // VRG-252: a Connect tap — the tag was read and carries no per-tap proof.
  nfc: Nfc,
};

import { statusChipSx } from 'components/pages/certificates/statusChipSx';

interface ScanStatusChipProps {
  status: ScanStatusMeta;
  className?: string;
  /**
   * Makes the chip a tap target that explains the state (VERA-628's `SealOpenedDrawer`
   * for "Opened"). Omit on surfaces that don't own such a sheet — the chip then renders
   * as a plain, non-interactive status, matching how `TagOpenedBadge` treats its own
   * optional `onClick`.
   */
  onClick?: () => void;
}

/**
 * The scan-status pill. ONE component for every surface that shows a scan outcome —
 * currently the Scans list row and the map's selected-scan card (VERA-476).
 *
 * It owns the styling so callers never pass colours: previously both sites called
 * `StatusBadge` with a hand-computed `colors` override, which is the second-hand-roll
 * pattern working-conventions §36 exists to stop. Palette lives on the shared
 * `getScanStatus` resolver in `@repo/ui`, so the chip, the map pin and the filter
 * options can't drift apart.
 *
 * Treatment matches the app's certificate status pills and is defined once in
 * `statusChipSx`: pale fill, the ramp's DEEP tone on the border, and a plain black
 * label (VERA-628). The border carries the colour signal so the label keeps full
 * black-on-pale contrast.
 *
 * Renders nothing when the status doesn't earn a chip — `verified` is the expected
 * outcome, so absence of a chip IS the signal.
 */
export const ScanStatusChip: React.FC<ScanStatusChipProps> = ({ status, className, onClick }) => {
  if (!status?.showChip) return null;

  const Icon = status.icon ? ICONS[status.icon] : undefined;

  return (
    <Chip
      className={className}
      label={
        Icon ? (
          // Icon + label, never icon alone. A glyph on its own would make the reader
          // learn a vocabulary; the word carries the meaning and the icon speeds
          // recognition and separates the two error states from each other.
          <>
            {/* The icon takes the SATURATED hue (`status.color`), not the label colour.
                That's what lets a chip carry a red alert signal while keeping its label
                near-black and legible — the Invalid chip is transparent-filled precisely
                so the red lives in the glyph rather than a filled block. */}
            <Icon size={14} color={status.color} />
            <span style={{ marginLeft: 5 }}>{status.label}</span>
          </>
        ) : (
          status.label
        )
      }
      // Stop the tap reaching the row/map behind the chip: inside the map's stack list
      // the row is itself interactive, so a bare bubbling click would open the sheet AND
      // change the selection.
      onClick={
        onClick
          ? e => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      // Metrics + the border/label colour rule come from the one shared source, which
      // `StatusBadge` also consumes (VERA-628, §36). Scan labels are authored copy.
      sx={{
        ...statusChipSx(status.chip, true),
        ...(onClick ? { cursor: 'pointer' } : {}),
      }}
    />
  );
};

export default ScanStatusChip;
